import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import agent from '../../app/api/agent';
import { getApiErrorMessage } from '../../app/api/error';
import { SafeSaveBar as SaveBar } from '../../app/components/SafeAppBridge';
import type {
    CollectionMappingListItem,
    CollectionMappingListResult,
    SelectedCollectionMapping,
} from '../../app/models/CollectionMapping';
import type { FitmentFieldValues } from '../../app/utils/fitmentFields';
import { PageQueryCache, isBrowseRequestCancelled } from './PageQueryCache';
import MappingFilters, { type AppliedMappingFilter, type MappingFilter, type MappingSortOption } from './MappingFilters';
import ProductThumbnail from '../../app/components/ProductThumbnail';
import { choiceValues, fieldChecked, usePolarisEvents } from '../../app/components/polaris';
import ErrorPreviewControls from './ErrorPreviewControls';

interface CollectionMappingsPanelProps {
    shopDomain?: string;
    cache: PageQueryCache<CollectionMappingListResult>;
    onMappingSaved?: () => void;
    hasVehicleContext: boolean;
    selectedVehicleLabel: string;
    year: string;
    yearTo: string;
    make: string;
    model: string;
    submodel: string;
    fieldValues: FitmentFieldValues;
    onDirtyChange?: (dirty: boolean) => void;
}

const emptyMapping = (): SelectedCollectionMapping => ({
    state: 'unmapped',
    collectionId: null,
    collectionTitle: '',
    collectionHandle: '',
    selectedVehicleCount: 0,
    mappedVehicleCount: 0,
});

const showToast = (message: string) => window.shopify?.toast?.show?.(message);

/**
 * Collection-side mapping surface. The selected vehicle is owned by the parent
 * Products page, while this component owns collection browsing and the single
 * mapping draft. Keeping those responsibilities separate prevents product
 * bulk-selection behavior from leaking into collection mapping.
 */
const CollectionMappingsPanel: React.FC<CollectionMappingsPanelProps> = ({
    shopDomain,
    cache,
    onMappingSaved,
    hasVehicleContext,
    selectedVehicleLabel,
    year,
    yearTo,
    make,
    model,
    submodel,
    fieldValues,
    onDirtyChange,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [collections, setCollections] = useState<CollectionMappingListItem[]>([]);
    const [mapping, setMapping] = useState<SelectedCollectionMapping>(emptyMapping);
    const [draftCollectionId, setDraftCollectionId] = useState<number | null>(null);
    const [draftChanged, setDraftChanged] = useState(false);
    const [query, setQuery] = useState('');
    const [collectionType, setCollectionType] = useState<string[]>([]);
    const [sortSelected, setSortSelected] = useState(['title asc']);
    const [mappedOnly, setMappedOnly] = useState(false);
    const [page, setPage] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [loadError, setLoadError] = useState('');
    const [mappingError, setMappingError] = useState('');
    const latestRequestId = useRef(0);
    const previousSelectionKey = useRef('');
    const draftChangedRef = useRef(draftChanged);

    useEffect(() => {
        draftChangedRef.current = draftChanged;
        onDirtyChange?.(draftChanged);
    }, [draftChanged, onDirtyChange]);

    const selectionKey = useMemo(() => JSON.stringify({
        year,
        yearTo,
        make,
        model,
        submodel,
        fields: Object.entries(fieldValues).sort(([left], [right]) => left.localeCompare(right)),
    }), [fieldValues, make, model, submodel, year, yearTo]);

    const loadCollections = useCallback(async (resetDraft: boolean) => {
        const requestId = ++latestRequestId.current;
        const cacheKey = JSON.stringify({
            page, query, mappedOnly, selectionKey, sort: sortSelected[0], collectionType: collectionType[0] ?? '',
        });
        setLoading(cache.get(cacheKey) === undefined);
        setLoadError('');
        try {
            const result = await cache.load(cacheKey, signal => agent.CollectionMappings.list(
                page,
                query,
                mappedOnly,
                year,
                yearTo,
                make,
                model,
                submodel,
                fieldValues,
                sortSelected[0],
                collectionType[0] ?? '',
                signal,
            ));
            if (requestId !== latestRequestId.current) return;

            setCollections(result.collections);
            setMapping(result.mapping);
            setHasNext(result.hasNext);
            setTotalCount(result.totalCount);
            if (resetDraft || !draftChangedRef.current) {
                setDraftCollectionId(
                    result.mapping.state === 'mapped'
                        ? result.mapping.collectionId ?? null
                        : null,
                );
                setDraftChanged(false);
            }
        } catch (error: any) {
            if (requestId === latestRequestId.current && !isBrowseRequestCancelled(error)) {
                const message = await getApiErrorMessage(error, 'Collections are temporarily unavailable.');
                if (requestId === latestRequestId.current) setLoadError(message);
            }
        } finally {
            if (requestId === latestRequestId.current) setLoading(false);
        }
    }, [cache, selectionKey, collectionType, fieldValues, make, mappedOnly, model, page, query, sortSelected, submodel, year, yearTo]);

    useEffect(() => {
        const selectionChanged = previousSelectionKey.current !== selectionKey;
        previousSelectionKey.current = selectionKey;

        if (selectionChanged && page !== 1) {
            setPage(1);
            return;
        }

        if (selectionChanged) {
            setMappingError('');
            setMappedOnly(false);
            setDraftChanged(false);
        }

        loadCollections(selectionChanged);
        return () => {
            latestRequestId.current += 1;
            cache.cancel();
        };
    }, [cache, loadCollections, page, selectionKey]);

    const selectCollection = useCallback((collectionId: number) => {
        if (!hasVehicleContext) {
            setMappingError('Select a vehicle above before assigning a collection.');
            return;
        }
        setDraftCollectionId(collectionId);
        setDraftChanged(true);
    }, [hasVehicleContext]);

    const removeMapping = useCallback(() => {
        if (!hasVehicleContext) return;
        setDraftCollectionId(null);
        setDraftChanged(true);
    }, [hasVehicleContext]);

    const discardChanges = useCallback(() => {
        setMappingError('');
        setDraftCollectionId(mapping.state === 'mapped' ? mapping.collectionId ?? null : null);
        setDraftChanged(false);
    }, [mapping]);

    const saveMapping = useCallback(async () => {
        if (!hasVehicleContext || !draftChanged) return;

        setSaving(true);
        try {
            const saved = await agent.CollectionMappings.saveMapping({
                collectionId: draftCollectionId,
                year,
                yearTo,
                make,
                model,
                submodel,
                fieldValues,
            });
            cache.clear();
            onMappingSaved?.();
            setMapping(saved);
            setMappingError('');
            setDraftCollectionId(saved.collectionId ?? null);
            setDraftChanged(false);
            showToast(saved.state === 'mapped'
                ? `Collection mapping saved for ${selectedVehicleLabel}`
                : `Collection mapping removed for ${selectedVehicleLabel}`);
            await loadCollections(true);
        } catch (error: any) {
            cache.clear();
            onMappingSaved?.();
            setMappingError(await getApiErrorMessage(error, 'The collection mapping could not be saved.'));
        } finally {
            setSaving(false);
        }
    }, [
        cache,
        onMappingSaved,
        draftChanged,
        draftCollectionId,
        fieldValues,
        hasVehicleContext,
        loadCollections,
        make,
        model,
        selectedVehicleLabel,
        submodel,
        year,
        yearTo,
    ]);

    const mappingSummary = (() => {
        if (!hasVehicleContext) {
            return null;
        }

        if (mapping.state === 'mixed') {
            return (
                <s-banner tone="warning" heading="Selected range has mixed collection mappings">
                    {mapping.mappedVehicleCount} of {mapping.selectedVehicleCount} vehicles are mapped to one or more collections.
                    Choosing a collection below will map every vehicle in the selected range to the same collection.
                </s-banner>
            );
        }

        return (
            <s-stack direction="inline" gap="small-200" alignItems="center">
                <s-text fontWeight="semibold">{selectedVehicleLabel}</s-text>
                {mapping.state === 'mapped' ? (
                    <s-badge tone="success">{`Mapped to “${mapping.collectionTitle}”`}</s-badge>
                ) : (
                    <s-badge>No collection mapping</s-badge>
                )}
            </s-stack>
        );
    })();

    const sortOptions: MappingSortOption[] = [
        { label: 'Collection title', value: 'title asc', directionLabel: 'A-Z' },
        { label: 'Collection title', value: 'title desc', directionLabel: 'Z-A' },
        { label: 'Products', value: 'products asc', directionLabel: 'Lowest to highest' },
        { label: 'Products', value: 'products desc', directionLabel: 'Highest to lowest' },
        { label: 'Assigned vehicles', value: 'vehicles asc', directionLabel: 'Lowest to highest' },
        { label: 'Assigned vehicles', value: 'vehicles desc', directionLabel: 'Highest to lowest' },
    ];

    const clearCollectionType = () => { setCollectionType([]); setPage(1); };
    const clearQuery = () => { setQuery(''); setPage(1); };
    const filters: MappingFilter[] = [{
        key: 'collectionType',
        label: 'Collection type',
        filter: (
            <s-choice-list
                label="Collection type"
                labelAccessibilityVisibility="exclusive"
                values={collectionType}
                onChange={(event) => { setCollectionType(choiceValues(event)); setPage(1); }}
            >
                <s-choice value="manual">Manual</s-choice>
                <s-choice value="smart">Smart</s-choice>
            </s-choice-list>
        ),
    }];
    const appliedFilters: AppliedMappingFilter[] = collectionType.length > 0 ? [{
        key: 'collectionType',
        label: `Collection type: ${collectionType[0] === 'manual' ? 'Manual' : 'Smart'}`,
        onRemove: clearCollectionType,
    }] : [];

    const tableEvents = usePolarisEvents({
        nextpage: () => { if (hasNext) setPage(current => current + 1); },
        previouspage: () => setPage(current => Math.max(1, current - 1)),
    });
    const bannerEvents = usePolarisEvents({
        dismiss: () => { setLoadError(''); setMappingError(''); },
    });
    const openCollection = (collectionId: number) => navigate(`/products/collections/${collectionId}`, {
        state: { from: `${location.pathname}${location.search}` },
    });

    return (
        <>
            <SaveBar id="collection-mapping-save-bar" open={draftChanged}>
                <button onClick={discardChanges} disabled={loading || saving}>Discard</button>
                <button variant="primary" onClick={saveMapping} disabled={loading || saving}>Save</button>
            </SaveBar>

            <s-stack gap="base">
                <ErrorPreviewControls search={location.search} shopDomain={shopDomain}
                    cases={[
                        { label: 'Preview collection loading error', onShow: () => { setMappingError(''); setLoadError('Sample error: Shopify is temporarily unavailable.'); } },
                        { label: 'Preview collection save error', onShow: () => { setLoadError(''); setMappingError('Sample error: The collection mapping could not be saved.'); } },
                    ]}
                    onClear={() => { setLoadError(''); setMappingError(''); }} />
                {(loadError || mappingError) && (
                    <s-banner ref={bannerEvents} tone="critical" dismissible
                        heading={mappingError ? 'Collection mapping needs attention' : 'Couldn’t load collections'}>
                        {mappingError && <s-paragraph>{mappingError} {hasVehicleContext ? 'Review your selection and try saving again.' : ''}</s-paragraph>}
                        {loadError && <s-paragraph>{loadError} Try loading the collections again.</s-paragraph>}
                        {loadError && (
                            <s-button slot="secondary-actions" loading={loading} onClick={() => { void loadCollections(false); }}>
                                Retry loading collections
                            </s-button>
                        )}
                    </s-banner>
                )}
                {!loadError && !mappingError && mappingSummary}

                {hasVehicleContext && (
                    <s-stack direction="inline" justifyContent="space-between" alignItems="center" gap="base">
                        <s-stack gap="none">
                            <s-text fontWeight="semibold">Collection mapping</s-text>
                            <s-text color="subdued">
                                Associate the selected vehicle with one Shopify collection. Product mappings remain independent.
                            </s-text>
                        </s-stack>
                        <s-button
                            onClick={removeMapping}
                            disabled={loading
                                || saving
                                || (draftCollectionId == null && (mapping.state === 'unmapped' || draftChanged))}
                        >
                            Remove mapping
                        </s-button>
                    </s-stack>
                )}

                <MappingFilters
                    hasVehicleContext={hasVehicleContext}
                    mappedOnly={mappedOnly}
                    onMappedOnlyChange={(value) => { setMappedOnly(value); setPage(1); }}
                    sortOptions={sortOptions}
                    sortSelected={sortSelected}
                    onSort={(value) => { setSortSelected(value); setPage(1); }}
                    queryValue={query}
                    queryPlaceholder="Search collections"
                    onQueryChange={(value) => { setQuery(value); setPage(1); }}
                    onClearAll={() => { clearQuery(); clearCollectionType(); setMappedOnly(false); }}
                    filters={filters}
                    appliedFilters={appliedFilters}
                />
                <s-text color="subdued">{`${totalCount.toLocaleString()} collection${totalCount === 1 ? '' : 's'}`}</s-text>
                <s-table ref={tableEvents} loading={loading} paginate hasNextPage={hasNext} hasPreviousPage={page > 1}>
                    <s-table-header-row>
                        <s-table-header listSlot="primary">Collection</s-table-header>
                        <s-table-header>Handle</s-table-header>
                        <s-table-header>Products</s-table-header>
                        <s-table-header>Assigned vehicles</s-table-header>
                    </s-table-header-row>
                    <s-table-body>
                        {collections.map((collection) => {
                            const checkboxId = `collection-mapping-${collection.id}`;
                            const linkId = `collection-link-${collection.id}`;
                            return (
                                <s-table-row key={collection.id} clickDelegate={hasVehicleContext ? checkboxId : linkId}>
                                    <s-table-cell>
                                        <s-stack direction="inline" gap="base" alignItems="center">
                                            {hasVehicleContext && (
                                                <s-checkbox
                                                    id={checkboxId}
                                                    accessibilityLabel={`Map ${selectedVehicleLabel} to ${collection.title}`}
                                                    checked={draftCollectionId === collection.id}
                                                    onChange={(event) => fieldChecked(event) ? selectCollection(collection.id) : removeMapping()}
                                                    disabled={loading || saving}
                                                />
                                            )}
                                            <ProductThumbnail source={collection.imageUrl} alt={collection.title} size="small" />
                                            <s-stack gap="none">
                                                <s-link id={linkId} onClick={() => openCollection(collection.id)}>{collection.title}</s-link>
                                                <s-text color="subdued">{collection.collectionType || 'Shopify collection'}</s-text>
                                            </s-stack>
                                        </s-stack>
                                    </s-table-cell>
                                    <s-table-cell>{collection.handle ? `/${collection.handle}` : '-'}</s-table-cell>
                                    <s-table-cell>{collection.productsCount.toLocaleString()}</s-table-cell>
                                    <s-table-cell>{collection.assignedVehicleCount.toLocaleString()}</s-table-cell>
                                </s-table-row>
                            );
                        })}
                    </s-table-body>
                </s-table>
            </s-stack>
        </>
    );
};

export default CollectionMappingsPanel;
